import React, { useState } from "react";
import { Modal, Upload, Table, Alert, Typography, Space, message } from "antd";
import { InboxOutlined, WarningOutlined } from "@ant-design/icons";
import { UiButton, UiTag } from "../../ui";
import GstSummaryTable from "../common/GstSummaryTable";
import { formatAmount } from "../common/money";
import { scanPharmacyInvoiceApi } from "../../../services/pharmacyPurchase.services";
import { filesToScannableImages } from "../common/pdfToImages";
import { getErrorMessage } from "../../../helpers/error";


const ACCEPTED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp"];
const ACCEPTED_MIME = ["image/png", "image/jpeg", "image/webp"];
const ACCEPTED = ACCEPTED_EXTENSIONS.join(",");
/* The hint and the refusal are both written from the list above, so what the
   screen says cannot drift from what the check does. */
const SUPPORTED_LABEL = ACCEPTED_EXTENSIONS.join(", ");
const UPLOAD_HINT = `${SUPPORTED_LABEL} are only allowed`;

const isSupportedFile = (file) => {
  const name = String(file?.name || "").toLowerCase();
  const type = String(file?.type || "").toLowerCase();
  if (ACCEPTED_MIME.includes(type)) return true;
  /* Some browsers hand over an empty type for a dragged file, so the extension
     is the fallback rather than the first thing tried. */
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const ScanInvoiceModal = ({ open, setOpen, onUse }) => {
  const [fileList, setFileList] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);

  const close = () => {
    setOpen(false);
    setFileList([]);
    setResult(null);
  };

  const handleScan = async () => {
    if (!fileList.length) {
      message.warning("Add at least one invoice file.");
      return;
    }

    setScanning(true);

    let images;
    try {
      // Images pass straight through; the conversion step is kept so PDF
      // support can be switched back on at ACCEPTED_EXTENSIONS alone.
      images = await filesToScannableImages(
        fileList.map((file) => file.originFileObj || file)
      );
    } catch (err) {
      setScanning(false);
      message.error(
        "Could not read those files. Try a clearer photo or screenshot of the invoice."
      );
      return;
    }

    if (!images.length) {
      setScanning(false);
      message.error("No readable pages were found in those files.");
      return;
    }

    const formData = new FormData();
    // Repeated key, matching the documented invoice_images[] field.
    images.forEach((file) => formData.append("invoice_images", file));

    scanPharmacyInvoiceApi(formData)
      .then((res) => {
        const data = res?.data?.data ?? res?.data;
        setResult(data);
        message.success(res?.data?.message || "Invoice scanned successfully.");
      })
      .catch((err) => message.error(getErrorMessage(err)))
      .finally(() => setScanning(false));
  };

  const items = result?.items || [];
  const needsReview = items.filter((item) => item?.needs_review);

  const columns = [
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
      render: (value, record) => (
        <Space size={4}>
          <span>{value || "-"}</span>
          {record?.needs_review ? (
            <UiTag tone="warning" size="sm">
              Review
            </UiTag>
          ) : null}
        </Space>
      ),
    },
    { title: "Batch", dataIndex: "batch_number", key: "batch_number" },
    { title: "Expiry", dataIndex: "expiry", key: "expiry" },
    { title: "Qty", dataIndex: "qty", key: "qty", align: "right" },
    {
      title: "MRP",
      dataIndex: "mrp",
      key: "mrp",
      align: "right",
      render: (v) => formatAmount(v || 0),
    },
    {
      title: "Rate",
      dataIndex: "purchase_rate",
      key: "purchase_rate",
      align: "right",
      render: (v) => formatAmount(v || 0),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (v) => formatAmount(v || 0),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={close}
      title="Upload Invoice"
      width={result ? 960 : 560}
      destroyOnClose
      footer={
        <Space>
          <UiButton variant="secondary" onClick={close}>
            Close
          </UiButton>
          {result ? (
            <UiButton onClick={() => onUse?.(result)}>Use These Details</UiButton>
          ) : (
            <UiButton loading={scanning} onClick={handleScan}>
              Scan
            </UiButton>
          )}
        </Space>
      }
    >
      {!result ? (
        <Upload.Dragger
          multiple
          accept={ACCEPTED}
          fileList={fileList}
          /*
           * The picker's `accept` only filters the file dialog - a drag and drop
           * bypasses it entirely - so the check is here, where every route in
           * passes. A rejected file is kept out of the list rather than added
           * and struck through: it was never going to be scanned.
           */
          beforeUpload={(file, batch) => {
            const rejected = (batch || [file]).filter((f) => !isSupportedFile(f));
            /* One message per drop, not one per bad file. */
            if (rejected.length && file === rejected[0]) {
              message.error(
                rejected.length === 1
                  ? `"${rejected[0].name}" is not a supported file. Only ${SUPPORTED_LABEL} are allowed.`
                  : `${rejected.length} files were not added. Only ${SUPPORTED_LABEL} are allowed.`
              );
            }
            return isSupportedFile(file) ? false : Upload.LIST_IGNORE;
          }}
          onChange={({ fileList: next }) => setFileList(next)}
          onRemove={(file) =>
            setFileList((prev) => prev.filter((f) => f.uid !== file.uid))
          }
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag invoice files here
          </p>
          <p className="ant-upload-hint">{UPLOAD_HINT}</p>
        </Upload.Dragger>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {needsReview.length ? (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={`${needsReview.length} row${
                needsReview.length > 1 ? "s" : ""
              } need checking before use`}
              description="The scan was unsure about these lines. Check them against the paper invoice."
            />
          ) : null}

          {result?.validation && result.validation.grand_total_matches === false ? (
            <Alert
              type="error"
              showIcon
              message="Totals do not match the invoice"
              description="The line items do not add up to the printed grand total. Verify before saving."
            />
          ) : null}

          <div>
            <Typography.Text type="secondary">Vendor</Typography.Text>
            <div>
              <Typography.Text strong>
                {result?.invoice?.vendor_name || "-"}
              </Typography.Text>
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Invoice {result?.invoice?.invoice_number || "-"} ·{" "}
              {result?.invoice?.invoice_date || "-"} ·{" "}
              {result?.invoice?.total_items ?? items.length} items
            </Typography.Text>
          </div>

          <Table
            size="small"
            rowKey={(row) => row.s_no ?? row.batch_number}
            columns={columns}
            dataSource={items}
            pagination={false}
            scroll={{ x: "max-content", y: 320 }}
          />

          <GstSummaryTable rows={result?.gst_summary} items={items} />

          {result?.bill_summary ? (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid #f0f0f0",
                paddingTop: 12,
              }}
            >
              <Typography.Text strong>Grand Total</Typography.Text>
              <Typography.Text strong>
                {formatAmount(result.bill_summary.grand_total || 0)}
              </Typography.Text>
            </div>
          ) : null}
        </Space>
      )}
    </Modal>
  );
};

export default ScanInvoiceModal;
